<template>
    <form class="form-horizontal with-border" role="form" method="POST" :action="config.endpoints.login">
        <h3>{{config.translations.login}}</h3>

        <div class="mb-3">
            <input type="hidden" name="_token" :value="config.csrf_token"/>
            <div class="col-md-12">
                <RelativeDiv>
                    <input
                        ref="emailInput"
                        v-model="state.email"
                        @input="verifyEmail($event.target.value)"
                        id="email"
                        type="email"
                        class="form-control"
                        name="email"
                        :placeholder=config.translations.email
                        required
                        autofocus
                    />

                    <Spinner v-if="state.verifying"/>
                </RelativeDiv>

                <span class="invalid-feedback d-block" v-if="config.errors.email && !state.verificationError">
                  <strong>{{ config.errors.email }}</strong>
                </span>

                <span class="invalid-feedback d-block" v-if="state.verificationError">
                  <strong>{{ state.verificationError }}</strong>
                </span>
            </div>
        </div>

        <div class="mb-3" v-if="state.verified && hasAca">
            <div class="col-md-12">
                <RelativeDiv>
                    <PasswordField
                        id="password"
                        type="password"
                        class="form-control"
                        name="password"
                        :placeholder=config.translations.password
                        required
                        autofocus
                    />

                    <ForgotPassword
                        :link="config.endpoints.passwordReset"
                        :forgot="config.translations.forgot"
                    />
                </RelativeDiv>

                <span class="invalid-feedback d-block" v-if="config.errors.password">
                  <strong>{{ config.errors.password }}</strong>
                </span>
            </div>
        </div>

        <div class="row" v-if="state.verified === false && !state.verifying && !state.verificationError">
            <div class="col-xs-12">
                {{ config.translations.intro }}
            </div>
        </div>

        <div class="row" v-if="state.verified && isStaff">
            <div class="col-xs-12 extra-buttons">
                <p>{{ openIdText }}</p>
                <a v-if="isStaff"
                   :href="config.endpoints.staffLoginEndpoint"
                   class="btn btn-primary oauth-login">
                    {{ hasBothOpenIdLogins ? config.translations.btn_r_zone : `${config.translations.btn_long_login} ${config.translations.btn_r_zone}` }}
                </a>
            </div>
        </div>

        <div class="row" v-if="state.verified && (messageEmployee || messageStaff)">
            <div class="col-xs-12 extra-buttons">
                <p v-if="messageEmployee">{{ config.translations.profile_id_employee }}</p>
                <p v-if="messageStaff">{{ config.translations.profile_id_staff }}</p>
            </div>
        </div>

        <div class="row" v-if="state.verified && messageACA">
            <div class="col-xs-12 extra-buttons">
                <p v-html="messageACA"></p>
            </div>
        </div>

        <div class="row mb-3 align-items-center" v-if="state.verified && hasAca">
            <div class="col-md-8 col-8">
                <div class="checkbox checkbox-primary">
                    <input type="checkbox" name="remember" id="login-remember"/>
                    <label for="login-remember">{{ config.translations.remember_me }}</label>
                </div>
            </div>
            <div class="col-md-4 col-4 text-end">
                <button type="submit" class="btn btn-danger float-end">
                    {{ config.translations.login }}
                </button>
            </div>
        </div>
    </form>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from 'vue';
import validator from 'validator';
import Spinner from './Spinner.vue';
import ForgotPassword from './ForgotPassword.vue';
import PasswordField from './PasswordField.vue';
import RelativeDiv from './RelativeDiv.vue';

const props = defineProps({
    config: {
        type: Object,
        required: true,
        validator(value) {
            return value.endpoints &&
                value.errors &&
                value.values &&
                value.csrf_token &&
                value.translations;
        }
    }
});

// Reactive state
const state = reactive({
    email: '',
    loginTypes: [],
    loginMessages: {},
    verified: false,
    verifying: false,
    verificationError: false
});

// Refs
const emailInput = ref(null);

// Computed properties
const messageACA = computed(() => state.loginMessages.aca || false);
const hasAca = computed(() => (state.loginTypes.find(x => x === 'aca') && !messageACA.value) || false);
const isStaff = computed(() => state.loginTypes.find(x => x === 'staff') || false);
const isEmployee = computed(() => state.loginTypes.find(x => x === 'employee') || false);
const messageStaff = computed(() => state.loginMessages.staff || false);
const messageEmployee = computed(() => state.loginMessages.employee || false);
const hasBothOpenIdLogins = computed(() => isStaff.value && isEmployee.value);
const openIdText = computed(() => props.config.translations.login_rzone);

function fetchLoginTypes(email) {
    const { endpoints } = props.config;

    state.verifying = true;
    fetch(
        `${endpoints.loginTypes}?email=${encodeURIComponent(email)}`,
        {
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        }
    )
        .then(response => response.json())
        .then(json => {
            if (json.types && json.types.length) {
                state.email = email;
                state.loginTypes = json.types;
                state.loginMessages = [];
                state.verified = true;
                state.verifying = false;
                return;
            }

            if (json.errors && json.errors.length) {
                state.email = email;
                state.loginTypes = [];
                state.loginMessages = json.errors;
                state.verified = true;
                state.verifying = false;
                return;
            }

            const { translations } = props.config;
            resetState({ verificationError: translations.verify_failed });
        });
}

function resetState(customState = {}) {
    Object.assign(state, {
        loginTypes: [],
        loginMessages: {},
        verified: false,
        verifying: false,
        verificationError: false,
        ...customState
    });
}

function verifyEmail(email) {
    if (validator.isEmail(email)) {
        fetchLoginTypes(email);
        return;
    }

    resetState();
}

onMounted(() => {
    const { values } = props.config;
    if (values.email) {
        verifyEmail(values.email);
    }
});
</script>
